import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Typography,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { StartTimeEndTimePart } from '@/components/StartTimeEndTimePart/StartTimeEndTimePart';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { CLIP_REVIEW_STATES } from '@/constants';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useProgramsByIds } from '@/features/programs/hooks/useProgramsByIds';
import { TReviewStateValues } from '@/features/segments/types';
import { downloadFromUrl } from '@/helpers/download';
import { useReport } from '@/stores/report-store';

import { usePatchClip } from '../../api/patch-clips';
import { useRelaunchClip } from '../../api/relaunch-clip';
import { useClipController } from '../../hooks/useClipController';
import { TPatchClip } from '../../types';
import { ClipMiniCardCardActions } from '../ClipBody/ClipMiniCardBase/components/ClipMiniCardCardActions';

export function ClipDetailCardHeader() {
  const { t } = useTranslation();
  const { activeClip, setActiveClip, setClipItems } = useBroadcastScheduleProgram();
  const { mutateAsync: patchSegment } = usePatchClip();
  const { setReport, setReportedId, setReportType } = useReport();
  const { programsById } = useProgramsByIds([activeClip?.program_id]);
  const { clipDialog, clipRelaunchDialog, handleDeleteClip } = useClipController();
  const { mutate: relaunchClip } = useRelaunchClip();

  const selectedProgram = activeClip?.program_id
    ? programsById.get(activeClip.program_id)
    : undefined;

  const handleStatusChange = async (status: string) => {
    if (status === '5') {
      setReportType('clip');
      setReport(true);
      setReportedId(activeClip?.id || '');
    }
    const body: TPatchClip = {
      id: activeClip?.id || '',
      review_state: parseInt(status) as TReviewStateValues,
      media_type: activeClip?.media_type || '',
    };

    await patchSegment(body).then(data => {
      setActiveClip(prev => {
        if (!prev) return prev;
        return { ...prev, review_state: data.review_state };
      });
      setClipItems(prev =>
        prev.map(item => {
          if (item.id === activeClip?.id) {
            return { ...item, review_state: data.review_state };
          }
          return item;
        })
      );
    });
  };

  const handleOpenDialog = (item: string) => {
    clipDialog.open(item);
  };

  if (!activeClip) return <></>;

  return (
    <>
      <div className="flex gap-1">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {activeClip?.media_type.includes(ChannelMediaTypeEnum.TV) ? (
            <SupportTvIcon />
          ) : (
            <SupportRadioIcon />
          )}
          <StatusSeenChipInTable item={activeClip} review_state={activeClip?.review_state || 0} />
          <Typography
            fontSize={FontSizeEnum.Body3}
            color={FontColorEnum.Dark10}
            weight={FontWeightEnum.Bold}
          >
            {selectedProgram?.channel?.name}
          </Typography>
          <StartTimeEndTimePart
            startDateTime={activeClip?.start_datetime}
            endDateTime={activeClip?.end_datetime}
          />
        </div>

        <div className="flex items-center justify-end" data-id="clip-detail-header-actions">
          <StatusSeenChipSelect
            value={activeClip?.review_state || 0}
            onChange={handleStatusChange}
            asIcon
            listOrientation="left"
            reviewStates={CLIP_REVIEW_STATES}
          />
          <ClipMiniCardCardActions
            item={activeClip}
            onDelete={handleOpenDialog}
            onDownload={downloadFromUrl}
            onRelaunch={clipRelaunchDialog.open}
            size={IconButtonSizeEnum.SM}
          />
        </div>
      </div>
      <ModalDialog
        isOpen={clipDialog.isOpen}
        onClose={clipDialog.close}
        onAction={() => handleDeleteClip(activeClip.id)}
        heading={t('Eliminar clip')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar el clip seleccionado?')} />
      </ModalDialog>

      <RelaunchEnrichmentsModal
        isOpen={clipRelaunchDialog.isOpen}
        onClose={clipRelaunchDialog.close}
        onAction={enrichments => relaunchClip({ id: activeClip.id, enrichments })}
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        allowAdvertising={false}
        isRadio={!activeClip?.media_type.includes(ChannelMediaTypeEnum.TV)}
      />
    </>
  );
}
